import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { resetSchedule } from "./resetScheduleOptimized.js";
import "./schedule.css";
import AddStudentDialog from "./schedule/AddStudentDialog.js";
import ManageStudentDialog from "./schedule/ManageStudentDialog.js";
import SaveScheduleModal from "./SaveScheduleModal.js";
import ScheduleHeader from "./schedule/ScheduleHeader.js";
import DayView from "./schedule/DayView.js";
import WeekView from "./schedule/WeekView.js";
import { days, hours, getAvailableHours, calculateStudentsInCells, findCoachByIdOrName, coachesForDay, normalizeStudents } from "./schedule/utils.js";
import useHistory from "../hooks/useHistory.js";
import { API_URL } from "../config/api.js";

/**
 * ==============================================================================
 * SCHEDULE COMPONENT - Hauptkomponente für die Trainingsplan-Verwaltung
 * ==============================================================================
 *
 * Diese Komponente ist die zentrale Schnittstelle für:
 * - Anzeige des Wochentrainingsplans (Tages- oder Wochenansicht)
 * - Drag & Drop Zuweisung von Schülern zu Kursen
 * - Automatische Kursgenerierung via resetSchedule-Algorithmus
 * - Undo/Redo Funktionalität für Änderungen
 * - Speichern und Laden von Trainingsplänen
 *
 * ANSICHTEN:
 * - Day View: Zeigt einen einzelnen Tag mit max. 3 Trainer-Spalten
 * - Week View: Zeigt die ganze Woche in kompakter Grid-Ansicht
 *
 * STATE MANAGEMENT:
 * - students: Mit Undo/Redo History (useHistory Hook)
 * - coaches: Trainer-Liste
 * - schedule: Berechnete Kurseinteilung (Map: Tag+Stunde → Schüler-Array)
 * - studentsNotSet: Schüler die nicht automatisch zugewiesen werden konnten
 *
 * WICHTIGE FUNKTIONEN:
 * - moveStudent(): Verschiebt Schüler zu neuem Tag/Stunde/Trainer
 * - handleGenerateSchedule(): Startet automatische Kursgenerierung
 * - findCoachByIdOrName(): Löst Coach-Referenz (ID oder "Vorname Nachname")
 * - coachMatchesStudent(): Prüft ob Schüler einem bestimmten Trainer zugeordnet ist
 * - calculateStudentsInCells(): Berechnet studentsInCell-Map für Grid-Darstellung
 *
 * ==============================================================================
 */
const Schedule = ({ onNavigate }) => {
  // Use history hook for students state (enables undo/redo)
  const {
    state: students,
    setState: setStudentsWithHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    replaceState: replaceStudents,
  } = useHistory([]);

  const [coaches, setCoaches] = useState([]);
  const [filteredCoaches, setFilteredCoaches] = useState([]);
  const [viewMode, setViewMode] = useState("day");
  const [selectedDay, setSelectedDay] = useState("Montag");
  const [schedule, setSchedule] = useState([]);
  const [addingStudent, setAddingStudent] = useState({ day: null, hour: null });
  const [selectedStudentToAdd, setSelectedStudentToAdd] = useState("");
  const [displayedStudentName, setDisplayedStudentName] = useState("");
  const [studentsNotSet, setStudentsNotSet] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [managingStudent, setManagingStudent] = useState(null);
  const [dragConfirmation, setDragConfirmation] = useState(null); // { studentId, newDay, newHour, newCoachId }
  const [allowResetSchedule, setAllowResetSchedule] = useState(true); // User setting for Plan Generieren button

  // Calculate available hours based on coach availability
  const availableHoursForDay = useMemo(() => {
    return getAvailableHours(coaches, selectedDay);
  }, [coaches, selectedDay]);

  // Calculate course statistics
  const courseStats = useMemo(() => {
    // Count courses with at least 1 student
    const occupiedCourses = schedule.filter(
      (course) => course.students && course.students.length > 0
    ).length;

    // Count all possible courses (each coach-time combination)
    let possibleCourses = 0;
    coaches.forEach(coach => {
      if (Array.isArray(coach.availableTimes)) {
        possibleCourses += coach.availableTimes.length;
      }
    });

    return {
      occupiedCourses,
      possibleCourses
    };
  }, [schedule, coaches]);

  const availableHoursForWeek = useMemo(() => {
    return getAvailableHours(coaches, null);
  }, [coaches]);

  // --- Load and normalize students + coaches + schedule
  useEffect(() => {
    axios
      .get(`/api/students`)
      .then((res) => {
        const normalized = normalizeStudents(res.data);
        // Use replaceStudents for initial load (no history entry)
        replaceStudents(normalized);

        // Calculate not assigned students (students without day or hour)
        const notAssigned = normalized.filter(
          (s) => !s.day || s.hour === null || s.hour === undefined
        );
        setStudentsNotSet(notAssigned);

        console.log(
          "DEBUG students normalized (sample):",
          normalized.slice(0, 8)
        );
        console.log("DEBUG not assigned students:", notAssigned.length);
      })
      .catch((err) =>
        console.error("Fehler beim Abrufen der Schülerdaten:", err)
      );

    axios
      .get(`/api/coaches`)
      .then((res) => {
        const normalized = res.data.map((c) => ({
          ...c,
          _id: String(c._id).trim(),
        }));
        setCoaches(normalized);
        console.log(
          "DEBUG coaches normalized:",
          normalized.map((c) => ({
            id: c._id,
            name: `${c.firstName} ${c.lastName}`,
            availableTimes: c.availableTimes?.slice(0, 4),
          }))
        );
      })
      .catch((err) =>
        console.error("Fehler beim Abrufen der Trainerdaten:", err)
      );

    axios
      .get(`/api/schedule`)
      .then((res) => setSchedule(res.data))
      .catch((err) => console.error("Fehler beim Abrufen des Zeitplans:", err));

    // Fetch user settings for Plan Generieren button
    axios
      .get(`/api/user-settings`)
      .then((res) => {
        setAllowResetSchedule(res.data.allowResetSchedule);
      })
      .catch((err) => {
        console.error("Fehler beim Abrufen der Benutzereinstellungen:", err);
        // Default to true if settings not found
        setAllowResetSchedule(true);
      });
  }, []);

  // --- keep filteredCoaches in sync with selectedDay/coaches
  useEffect(() => {
    const filtered = coachesForDay(coaches, selectedDay);
    setFilteredCoaches(filtered);
  }, [selectedDay, coaches]);

  // --- Reset schedule handler
  const handleResetSchedule = async () => {
    setIsGenerating(true);
    try {
      // Clear history when resetting schedule
      const notAssignedStudents = await resetSchedule(setStudentsWithHistory, setSchedule);
      setStudentsNotSet(notAssignedStudents);
      console.log(
        "Liste der nicht zugewiesenen Schüler nach Reset:",
        notAssignedStudents
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // --- Save schedule handler
  const handleSaveSchedule = async ({ name, description }) => {
    try {
      await axios.post(`/api/saved-schedules`, {
        name,
        description,
      });
      toast.success(`Zeitplan "${name}" erfolgreich gespeichert`);
      setShowSaveModal(false);
    } catch (error) {
      console.error("Fehler beim Speichern des Zeitplans:", error);
      toast.error("Fehler beim Speichern des Zeitplans");
    }
  };

  // --- Move student (updates specific assignment, preserves others)
  const moveStudent = async (studentId, newDay, newHour, newCoachId, fromDay, fromHour) => {
    try {
      // Use the new assignments/replace endpoint to move student
      // Pass fromDay/fromHour so backend knows which assignment to update
      await axios.put(`/api/students/${studentId}/assignments/replace`, {
        day: newDay,
        hour: newHour,
        coach: newCoachId || null,
        fromDay,
        fromHour,
      });

      // Reload students to get updated assignments
      const response = await axios.get(`/api/students`);
      const normalized = normalizeStudents(response.data);

      // Save to history
      setStudentsWithHistory(normalized);

      // Recalculate schedule
      const fullSchedule = calculateStudentsInCells(normalized, days, hours);
      await axios.put(`/api/schedule`, { schedule: fullSchedule });

      toast.success("Schüler verschoben");
    } catch (error) {
      console.error(
        "Fehler beim Verschieben des Schülers oder Speichern des Zeitplans:",
        error
      );
      toast.error("Fehler beim Verschieben des Schülers");
    }
  };

  // --- Add student dialog handler (supports "add" or "move" action)
  const handleAddStudentToCell = async (action = "add") => {
    if (selectedStudentToAdd && addingStudent.day && addingStudent.hour) {
      try {
        if (action === "move") {
          // Replace all assignments with new one
          await axios.put(`/api/students/${selectedStudentToAdd}/assignments/replace`, {
            day: addingStudent.day,
            hour: addingStudent.hour,
            coach: addingStudent.coach || null,
          });
        } else {
          // Add to existing assignments
          await axios.post(`/api/students/${selectedStudentToAdd}/assignments`, {
            day: addingStudent.day,
            hour: addingStudent.hour,
            coach: addingStudent.coach || null,
          });
        }

        // Reload students to get updated assignments
        const response = await axios.get(`/api/students`);
        const normalized = normalizeStudents(response.data);

        replaceStudents(normalized);

        // Recalculate schedule
        const fullSchedule = calculateStudentsInCells(normalized, days, hours);
        setSchedule(fullSchedule);
        await axios.put(`/api/schedule`, { schedule: fullSchedule });

        toast.success(action === "move" ? "Schüler verschoben" : "Schüler hinzugefügt");

        setAddingStudent({ day: null, hour: null });
        setSelectedStudentToAdd("");
        setDisplayedStudentName("");
      } catch (error) {
        console.error("Fehler beim Hinzufügen des Schülers:", error);
        if (error.response && error.response.data && error.response.data.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error("Fehler beim Hinzufügen des Schülers");
        }
      }
    }
  };

  // --- Manage student handlers (for removing assignments)
  const handleManageStudent = (student) => {
    setManagingStudent(student);
  };

  const handleRemoveAssignment = async (studentId, day, hour) => {
    try {
      await axios.delete(`/api/students/${studentId}/assignments`, {
        data: { day, hour }
      });

      // Reload students
      const response = await axios.get(`/api/students`);
      const normalized = normalizeStudents(response.data);

      replaceStudents(normalized);

      // Recalculate schedule
      const fullSchedule = calculateStudentsInCells(normalized, days, hours);
      setSchedule(fullSchedule);
      await axios.put(`/api/schedule`, { schedule: fullSchedule });

      toast.success("Assignment removed");
      setManagingStudent(null);
    } catch (error) {
      console.error("Error removing assignment:", error);
      toast.error("Error removing assignment");
    }
  };

  const handleRemoveAllAssignments = async (studentId) => {
    try {
      const student = students.find(s => s._id === studentId);
      if (!student || !student.assignments) return;

      // Remove all assignments one by one
      for (const assignment of student.assignments) {
        await axios.delete(`/api/students/${studentId}/assignments`, {
          data: { day: assignment.day, hour: assignment.hour }
        });
      }

      // Reload students
      const response = await axios.get(`/api/students`);
      const normalized = normalizeStudents(response.data);

      replaceStudents(normalized);

      // Recalculate schedule
      const fullSchedule = calculateStudentsInCells(normalized, days, hours);
      setSchedule(fullSchedule);
      await axios.put(`/api/schedule`, { schedule: fullSchedule });

      toast.success("All assignments removed");
      setManagingStudent(null);
    } catch (error) {
      console.error("Error removing all assignments:", error);
      toast.error("Error removing all assignments");
    }
  };

  // --- Undo/Redo handlers
  const handleUndo = useCallback(() => {
    undo();
  }, [undo]);

  const handleRedo = useCallback(() => {
    redo();
  }, [redo]);

  // --- Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl+Y or Cmd+Shift+Z for redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // --- Render
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="schedule-container">
        <ScheduleHeader
          viewMode={viewMode}
          setViewMode={setViewMode}
          handleResetSchedule={handleResetSchedule}
          setShowSaveModal={setShowSaveModal}
          onNavigate={onNavigate}
          studentsNotSet={studentsNotSet}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          allowResetSchedule={allowResetSchedule}
        />

        <SaveScheduleModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveSchedule}
          studentCount={students.length}
          coachCount={coaches.length}
          courseCount={courseStats.occupiedCourses}
          possibleCourseCount={courseStats.possibleCourses}
        />

        {isGenerating && (
          <div className="generating-overlay">
            <div className="generating-message">
              <div className="spinner"></div>
              <h2>Erstelle Trainingsplan...</h2>
              <p>Bitte warten, dies kann einige Sekunden dauern.</p>
            </div>
          </div>
        )}

        {viewMode === "day" ? (
          <DayView
            selectedDay={selectedDay}
            filteredCoaches={filteredCoaches}
            students={students}
            coaches={coaches}
            moveStudent={moveStudent}
            setAddingStudent={setAddingStudent}
            setDisplayedStudentName={setDisplayedStudentName}
            setSelectedStudentToAdd={setSelectedStudentToAdd}
            onManageStudent={handleManageStudent}
            hours={availableHoursForDay}
          />
        ) : (
          <WeekView
            students={students}
            coaches={coaches}
            selectedDay={selectedDay}
            moveStudent={moveStudent}
            setAddingStudent={setAddingStudent}
            setDisplayedStudentName={setDisplayedStudentName}
            setSelectedStudentToAdd={setSelectedStudentToAdd}
            onManageStudent={handleManageStudent}
            hours={availableHoursForWeek}
          />
        )}

        {addingStudent.day && addingStudent.hour && (
          <AddStudentDialog
            students={students}
            displayedStudentName={displayedStudentName}
            setDisplayedStudentName={setDisplayedStudentName}
            selectedStudentToAdd={selectedStudentToAdd}
            setSelectedStudentToAdd={setSelectedStudentToAdd}
            handleAddStudentToCell={handleAddStudentToCell}
            closeDialog={() => setAddingStudent({ day: null, hour: null })}
            targetDay={addingStudent.day}
            targetHour={addingStudent.hour}
            targetCoach={addingStudent.coach}
          />
        )}

        {managingStudent && (
          <ManageStudentDialog
            student={managingStudent}
            coaches={coaches}
            onClose={() => setManagingStudent(null)}
            onRemoveAssignment={handleRemoveAssignment}
            onRemoveAllAssignments={handleRemoveAllAssignments}
          />
        )}

        {dragConfirmation && (
          <div className="modal-overlay" onClick={() => setDragConfirmation(null)}>
            <div className="add-student-dialog confirmation-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Schüler bereits zugewiesen</h3>
              <p>
                <strong>{dragConfirmation.student.firstName} {dragConfirmation.student.lastName}</strong> ist bereits zugewiesen:
              </p>
              <ul className="existing-assignments">
                {dragConfirmation.student.assignments.map((assignment, idx) => (
                  <li key={idx}>
                    📅 {assignment.day} {assignment.hour}:00
                  </li>
                ))}
              </ul>
              <p>Möchten Sie den Schüler <strong>verschieben</strong> oder <strong>hinzufügen</strong>?</p>
              <div className="confirmation-info" style={{ margin: '15px 0', fontSize: '14px' }}>
                <div style={{ padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '8px' }}>
                  <strong>Verschieben:</strong> Entfernt alle bisherigen Zuweisungen und weist nur {dragConfirmation.newDay} {dragConfirmation.newHour}:00 zu.
                </div>
                <div style={{ padding: '8px', backgroundColor: '#d1ecf1', borderRadius: '4px' }}>
                  <strong>Hinzufügen:</strong> Behält bisherige Zuweisungen und fügt {dragConfirmation.newDay} {dragConfirmation.newHour}:00 hinzu.
                </div>
              </div>
              <div className="dialog-buttons">
                <button
                  onClick={() => moveStudent(
                    dragConfirmation.studentId,
                    dragConfirmation.newDay,
                    dragConfirmation.newHour,
                    dragConfirmation.newCoachId,
                    "move"
                  )}
                  className="btn-move"
                >
                  Verschieben
                </button>
                <button
                  onClick={() => moveStudent(
                    dragConfirmation.studentId,
                    dragConfirmation.newDay,
                    dragConfirmation.newHour,
                    dragConfirmation.newCoachId,
                    "add"
                  )}
                  className="btn-add"
                >
                  Hinzufügen
                </button>
                <button onClick={() => setDragConfirmation(null)} className="btn-cancel">
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default Schedule;
